import { generateHandle } from './handles';
import { getSupabase } from './supabase';

const DEVICE_KEY = 'tst:device-id';
const DEVICE_SECRET_KEY = 'tst:device-secret';
const HANDLE_KEY = 'tst:handle';
const HOMEWORLD_KEY = 'tst:homeworld';
const BESTS_STORAGE_PREFIX = 'tst:bests:';
const GHOST_STORAGE_PREFIX = 'tst:ghost:pb:';

export const HOMEWORLDS = [
  'Tatoona',
  'Nabuna',
  'Coruska',
  'Hothar',
  'Endara',
  'Bespira',
  'Mustara',
  'Kashyra',
  'Kamira',
  'Dagova',
  'Mandalora',
  'Jakkara',
] as const;

export const DEFAULT_HOMEWORLD = 'Tatoona';

const HOMEWORLD_ALIASES: Record<string, (typeof HOMEWORLDS)[number]> = {
  Tatooine: 'Tatoona',
  Naboo: 'Nabuna',
  Coruscant: 'Coruska',
  Hoth: 'Hothar',
  Endor: 'Endara',
  Bespin: 'Bespira',
  Mustafar: 'Mustara',
  Kashyyyk: 'Kashyra',
  Kamino: 'Kamira',
  Dagobah: 'Dagova',
  Mandalore: 'Mandalora',
  Jakku: 'Jakkara',
};

export interface Identity {
  deviceId: string;
  deviceSecret: string;
  handle: string;
  homeworld: string;
  /** Active Supabase auth user for this browser session. */
  authUserId: string | null;
  /** Active gameplay owner. Null for guests. */
  userId: string | null;
}

let cached: Identity | null = null;

export async function ensureIdentity(): Promise<Identity> {
  return ensureIdentityFromStorage(false);
}

async function ensureIdentityFromStorage(rotatedSignedOutLink: boolean): Promise<Identity> {
  if (cached) return cached;

  const deviceId = readOrCreate(DEVICE_KEY, () => crypto.randomUUID());
  const deviceSecret = readOrCreate(DEVICE_SECRET_KEY, makeDeviceSecret);
  const handle = readOrCreate(HANDLE_KEY, generateHandle);
  const homeworld = normalizeHomeworld(readOrCreate(HOMEWORLD_KEY, generateHomeworld));

  let userId: string | null = null;
  let authUserId: string | null = null;
  let serverHandle = handle;
  let serverHomeworld = homeworld;

  const sb = getSupabase();
  if (sb) {
    const { data: { user } } = await sb.auth.getUser();
    authUserId = user?.id ?? null;
    userId = authUserId;

    if (authUserId) {
      const { data: u, error } = await sb.from('users').select('handle, homeworld').eq('id', authUserId).maybeSingle();
      if (error) {
        const { data: legacyUser } = await sb.from('users').select('handle').eq('id', authUserId).maybeSingle();
        if (legacyUser?.handle && typeof legacyUser.handle === 'string') {
          serverHandle = legacyUser.handle;
          writeLocal(HANDLE_KEY, serverHandle);
        }
      }
      if (u?.handle && typeof u.handle === 'string') {
        serverHandle = u.handle;
        writeLocal(HANDLE_KEY, serverHandle);
      }
      if (u?.homeworld && typeof u.homeworld === 'string') {
        serverHomeworld = normalizeHomeworld(u.homeworld);
        writeLocal(HOMEWORLD_KEY, serverHomeworld);
      }
    }

    const { data, error } = await sb.rpc('tst_upsert_device', {
      p_device_id: deviceId,
      p_handle: serverHandle,
      p_device_secret: deviceSecret,
    });

    if (error) {
      console.warn('[twin-suns-turbo] device registration failed', error.message);
    } else {
      const row = Array.isArray(data) ? data[0] : data;
      if (row && typeof row === 'object') {
        const registered = row as { handle?: unknown; homeworld?: unknown; user_id?: unknown };
        if (typeof registered.handle === 'string') {
          serverHandle = registered.handle;
          writeLocal(HANDLE_KEY, serverHandle);
        }
        if (typeof registered.homeworld === 'string') {
          serverHomeworld = normalizeHomeworld(registered.homeworld);
          writeLocal(HOMEWORLD_KEY, serverHomeworld);
        }
        userId = typeof registered.user_id === 'string' ? registered.user_id : null;
        if (!authUserId && userId) {
          if (rotatedSignedOutLink) userId = null;
          else {
            resetGuestIdentityLocal();
            return ensureIdentityFromStorage(true);
          }
        }
      }
    }

    const profile = await sb.rpc('tst_save_pilot_profile', {
      p_device_id: deviceId,
      p_device_secret: deviceSecret,
      p_handle: serverHandle,
      p_homeworld: serverHomeworld,
    });
    if (!profile.error) {
      const row = Array.isArray(profile.data) ? profile.data[0] : profile.data;
      if (row && typeof row === 'object') {
        const saved = row as { handle?: unknown; homeworld?: unknown; user_id?: unknown };
        if (typeof saved.handle === 'string') {
          serverHandle = saved.handle;
          writeLocal(HANDLE_KEY, serverHandle);
        }
        if (typeof saved.homeworld === 'string') {
          serverHomeworld = normalizeHomeworld(saved.homeworld);
          writeLocal(HOMEWORLD_KEY, serverHomeworld);
        }
        userId = typeof saved.user_id === 'string' ? saved.user_id : userId;
        if (!authUserId && userId) {
          if (rotatedSignedOutLink) userId = null;
          else {
            resetGuestIdentityLocal();
            return ensureIdentityFromStorage(true);
          }
        }
      }
    }
  }

  cached = {
    deviceId,
    deviceSecret,
    handle: serverHandle,
    homeworld: serverHomeworld,
    authUserId,
    userId: authUserId ?? userId,
  };
  return cached;
}

export function getIdentity(): Identity | null {
  return cached;
}

export function setHandleLocal(handle: string): void {
  writeLocal(HANDLE_KEY, handle);
  if (cached) cached.handle = handle;
}

export function setHomeworldLocal(homeworld: string): void {
  const normalized = normalizeHomeworld(homeworld);
  writeLocal(HOMEWORLD_KEY, normalized);
  if (cached) cached.homeworld = normalized;
}

export function setUserIdLocal(userId: string | null): void {
  if (cached) cached.userId = userId;
}

export function setAuthUserIdLocal(authUserId: string | null): void {
  if (cached) cached.authUserId = authUserId;
}

export function resetGuestIdentityLocal(): void {
  const previousDeviceId = readLocal(DEVICE_KEY);
  clearLocalTimingStateForFreshGuest(previousDeviceId);
  writeLocal(DEVICE_KEY, crypto.randomUUID());
  writeLocal(DEVICE_SECRET_KEY, makeDeviceSecret());
  writeLocal(HANDLE_KEY, generateHandle());
  writeLocal(HOMEWORLD_KEY, generateHomeworld());
  cached = null;
}

export function identityStorageScope(id: Identity | null): string | null {
  if (!id) return null;
  return id.authUserId
    ? `user:${id.authUserId}`
    : `device:${id.deviceId}`;
}

export function normalizeHomeworld(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  if (trimmed in HOMEWORLD_ALIASES) return HOMEWORLD_ALIASES[trimmed];
  return HOMEWORLDS.includes(trimmed as (typeof HOMEWORLDS)[number])
    ? trimmed
    : DEFAULT_HOMEWORLD;
}

export function generateHomeworld(): string {
  return HOMEWORLDS[Math.floor(Math.random() * HOMEWORLDS.length)];
}

/**
 * Picks a new random handle + homeworld for the current guest, writes both
 * to localStorage, and updates the cached identity. Caller is responsible
 * for syncing to the server (e.g. via `savePilotProfile`) so the rerolled
 * pair survives the next `ensureIdentity()` round-trip.
 */
export function rerollGuestIdentityLocal(): { handle: string; homeworld: string } {
  const handle = generateHandle();
  const homeworld = generateHomeworld();
  setHandleLocal(handle);
  setHomeworldLocal(homeworld);
  return { handle, homeworld };
}

export function __test__resetIdentity(): void {
  cached = null;
}

function readOrCreate(key: string, gen: () => string): string {
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
  } catch {
    /* storage unavailable — fall through to generate */
  }
  const value = gen();
  writeLocal(key, value);
  return value;
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function clearLocalTimingStateForFreshGuest(previousDeviceId: string | null): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && shouldClearTimingKeyForFreshGuest(key, previousDeviceId)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

function shouldClearTimingKeyForFreshGuest(key: string, previousDeviceId: string | null): boolean {
  return shouldClearScopedTimingKey(key, BESTS_STORAGE_PREFIX, previousDeviceId)
    || shouldClearScopedTimingKey(key, GHOST_STORAGE_PREFIX, previousDeviceId);
}

function shouldClearScopedTimingKey(key: string, prefix: string, previousDeviceId: string | null): boolean {
  if (!key.startsWith(prefix)) return false;
  if (previousDeviceId && key.endsWith(`:device:${previousDeviceId}`)) return true;
  return !key.includes(':user:') && !key.includes(':device:');
}

function makeDeviceSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
